import React, { useState, useEffect, useRef, useCallback } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { useOutletContext, useLocation } from "react-router-dom";
import { getData } from "../../../webService/webservice.js";
import { useStore } from "../../../Context/context.jsx";
import { exportDataToExcel } from "../../../utils/excel-Export.js";
import "../../../styles/Auth/sales-report.css";



const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};



export default function CustomerReport() {
    const location = useLocation();
    const { startDate, endDate } = useOutletContext();
    const { selectedStoreId } = useStore();
    const sessionToken = localStorage.getItem("Token");
    const role = localStorage.getItem("role");
    const isSuperAdmin = role === "SuperAdmin" || role === "SelfAdmin";
    const COLUMN_LABELS = {
        name: "Name",
        phoneNumber: "Phone",
        email: "Email",
        gender: "Gender",
        age: "Age",
        membership: "Membership",
        totalSpend: "Total Spend",
        totalBookings: "Total Bookings",
        lastActiveAt: "Last Active",
        branchName: "Branch",
        isActive: "Status",
        createdAt: "Since",
        action: "Action",
        serviceName: "Service Name",
        usageCount: "Usage Count",
        totalSpent: "Total Spent",
        firstServiceDate: "First Service",
        lastServiceDate: "Last Service",
    };

    const STANDARD_COLUMNS = ["name", "phoneNumber", "gender", "membership", "totalSpend", "totalBookings", "lastActiveAt", ...(isSuperAdmin ? ["branchName"] : []), "isActive", "createdAt", "action"];
    const FREQUENT_COLUMNS = ["name", "phoneNumber", "serviceName", "usageCount", "totalSpent", "firstServiceDate", "lastServiceDate", ...(isSuperAdmin ? ["branchName"] : [])];
    const ALL_COLUMNS = Array.from(new Set([...STANDARD_COLUMNS, ...FREQUENT_COLUMNS]));
    
    // ── Tabs ──
    const [activeTab, setActiveTab] = useState("all");
    const tabColumns = activeTab === "frequent-services" ? FREQUENT_COLUMNS : STANDARD_COLUMNS;

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const paramTab = queryParams.get("tab");
        if (paramTab) {
            const lowerTab = paramTab.toLowerCase();
            if (lowerTab === "all") setActiveTab("all");
            else if (lowerTab === "men" || lowerTab === "male") setActiveTab("men");
            else if (lowerTab === "women" || lowerTab === "female") setActiveTab("women");
            else if (lowerTab === "others") setActiveTab("others");
            else if (lowerTab === "membership") setActiveTab("membership");
            else if (lowerTab === "nonmembership" || lowerTab === "non-membership") setActiveTab("non-membership");
            else if (lowerTab === "inactive" || lowerTab === "churned" || lowerTab === "defected") setActiveTab("inactive");
            else if (lowerTab === "new") setActiveTab("new");
            else if (lowerTab === "old" || lowerTab === "existing") setActiveTab("old");
            else if (lowerTab === "frequent-services") setActiveTab("frequent-services");
        }
    }, [location.search]);

    // ── Data ──
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState({
        newCustomers: 0,
        existingCustomers: 0,
        men: 0,
        women: 0,
        others: 0,
        inactiveCustomers: 0,
        membershipCustomers: 0,
        nonMembershipCustomers: 0,
        totalCustomers: 0
    });
    const [loading, setLoading] = useState(false);

    // ── Pagination ──
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // ── Filters ──
    const [storeList, setStoreList] = useState([]);
    const [filterStoreId, setFilterStoreId] = useState("");
    const [tableSearch, setTableSearch] = useState("");

    // ── Table Column Visibility ──
    const [showColumnToggler, setShowColumnToggler] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState(
        Object.fromEntries(ALL_COLUMNS.map(c => [c, true]))
    );
    const columnTogglerRef = useRef(null);

    // ── Modal ──
    const [selectedRecord, setSelectedRecord] = useState(null);

    // ── Fetch Stores ──
    useEffect(() => {
        const fetchStores = async () => {
            try {
                const res = await getData("stores/stores", { limit: 1000 }, sessionToken);
                if (res?.data) {
                    setStoreList(Array.isArray(res.data) ? res.data : (res.data.results || []));
                }
            } catch (err) {
                console.error(err);
            }
        };
        fetchStores();
    }, [sessionToken]);

    // ── Click outside column toggler ──
    useEffect(() => {
        const handler = (e) => {
            if (columnTogglerRef.current && !columnTogglerRef.current.contains(e.target))
                setShowColumnToggler(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    // ── Fetch Report Data ──
    const fetchReport = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                startDate,
                endDate,
                page: currentPage,
                limit: rowsPerPage,
                tab: activeTab,
                search: tableSearch,
                storeId: isSuperAdmin ? filterStoreId : (filterStoreId || selectedStoreId || "")
            };

            const res = await getData("reports/reports/customer", params, sessionToken);

            if (res?.status === 200 || res?.success) {
                setData(res.data?.data || []);
                setSummary(res.data?.summary || {
                    newCustomers: 0,
                    existingCustomers: 0,
                    men: 0,
                    women: 0,
                    others: 0,
                    inactiveCustomers: 0,
                    membershipCustomers: 0,
                    nonMembershipCustomers: 0,
                    totalCustomers: 0
                });
                const pg = res.data?.pagination || {};
                setTotalItems(pg.totalItems || 0);
                setTotalPages(pg.totalPages || 1);
            } else {
                toast.error(res?.message || "Failed to fetch customer report");
            }
        } catch (err) {
            console.error(err);
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, currentPage, rowsPerPage, selectedStoreId, filterStoreId, tableSearch, activeTab, sessionToken, isSuperAdmin]);

    useEffect(() => {
        setCurrentPage(1);
    }, [startDate, endDate, filterStoreId, activeTab, tableSearch]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    // ── Export Excel ──
    const handleExportExcel = async () => {
        try {
            const params = {
                startDate,
                endDate,
                page: 1,
                limit: "all",
                tab: activeTab,
                search: tableSearch,
                storeId: isSuperAdmin ? filterStoreId : (filterStoreId || selectedStoreId || "")
            };

            const res = await getData("reports/reports/customer", params, sessionToken);
            if (res?.status === 200 || res?.success) {
                let exportData = [];
                if (activeTab === "frequent-services") {
                    exportData = (res.data?.data || []).map((c, idx) => ({
                        "S.No": idx + 1,
                        "Name": c.name || "—",
                        "Phone": c.phoneNumber || "—",
                        "Service Name": c.serviceName || "—",
                        "Usage Count": c.usageCount || 0,
                        "Total Spent": c.totalSpent?.toFixed(2) || "0.00",
                        "First Visit": formatDate(c.firstServiceDate),
                        "Last Visit": formatDate(c.lastServiceDate)
                    }));
                } else {
                    exportData = (res.data?.data || []).map((c, idx) => ({
                        "S.No": idx + 1,
                        "Name": c.name || "—",
                        "Phone": c.phoneNumber || "—",
                        "Gender": c.gender ? c.gender.toUpperCase() : "—",
                        "Membership": c.hasActiveMembership ? (c.activeMembershipName || "Active Member") : "Non-Member",
                        "Total Spend": c.totalSpend || 0,
                        "Total Bookings": c.totalBookings || 0,
                        "Last Active": formatDate(c.lastActiveAt),
                        "Branch": c.branchName || "—",
                        "Status": c.isActive ? "Active" : "Inactive",
                        "Since": formatDate(c.createdAt)
                    }));
                }

                const fileName = `Customer_Report_${activeTab}_${startDate}_to_${endDate}`;
                exportDataToExcel(exportData, "Customer Report", `${fileName}.xlsx`);
            } else {
                toast.error("Failed to fetch data for export");
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to export Excel");
        }
    };

    const renderPageNumbers = () => {
        const pages = [];
        const window = 2;
        let start = Math.max(1, currentPage - window);
        let end = Math.min(totalPages, currentPage + window);

        if (currentPage <= window + 1) end = Math.min(totalPages, 2 * window + 1);
        if (currentPage >= totalPages - window) start = Math.max(1, totalPages - 2 * window);

        for (let i = start; i <= end; i++) {
            pages.push(
                <button
                    key={i}
                    className={`page-btn ${i === currentPage ? "active" : ""}`}
                    onClick={() => setCurrentPage(i)}
                >
                    {i}
                </button>
            );
        }
        return pages;
    };

    const startItem = (currentPage - 1) * rowsPerPage + 1;
    const endItem = Math.min(currentPage * rowsPerPage, totalItems);

    return (
        <div className="sales-report-container">
            {/* ── Header ── */}
            <div className="sales-report-header" style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 10 }}>
                <div className="sales-report-title-block">
                    <h1>Customer Report</h1>
                    <p>Analyze salon customer demographics, spent details, and visits</p>
                </div>
                <div className="sales-report-actions">
                    <button
                        className="sales-action-btn secondary"
                        onClick={handleExportExcel}
                    >
                        <Icon icon="mdi:microsoft-excel" width="16" />
                        Export Excel
                    </button>
                    <button
                        className="sales-action-btn primary"
                        onClick={fetchReport}
                    >
                        <Icon icon="mdi:refresh" width="16" />
                        Refresh
                    </button>
                </div>
            </div>

            {/* ── Summary Cards ── */}
            <div className="sales-summary-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
                <div className="sales-summary-card card-total" onClick={() => setActiveTab("all")} style={{ background: '#e0e7ff', borderColor: '#c7d2fe', cursor: 'pointer' }}>
                    <div className="sales-summary-icon" style={{ color: '#4338ca' }}>
                        <Icon icon="mdi:account-group" width="26" />
                    </div>
                    <div className="sales-summary-info">
                        <h3>Total Customers</h3>
                        <p className="sales-summary-value">{summary.totalCustomers || 0}</p>
                    </div>
                </div>
                <div className="sales-summary-card card-avg" onClick={() => setActiveTab("new")} style={{ background: '#dcfce7', borderColor: '#bbf7d0', cursor: 'pointer' }}>
                    <div className="sales-summary-icon" style={{ color: '#15803d' }}>
                        <Icon icon="mdi:account-plus" width="26" />
                    </div>
                    <div className="sales-summary-info">
                        <h3>New Customers</h3>
                        <p className="sales-summary-value">{summary.newCustomers || 0}</p>
                    </div>
                </div>
                <div className="sales-summary-card" onClick={() => setActiveTab("old")} style={{ background: '#fff3e0', borderColor: '#ffe0b2', cursor: 'pointer' }}>
                    <div className="sales-summary-icon" style={{ color: '#e65100' }}>
                        <Icon icon="mdi:account-clock" width="26" />
                    </div>
                    <div className="sales-summary-info">
                        <h3>Old Customers</h3>
                        <p className="sales-summary-value">{summary.existingCustomers || 0}</p>
                    </div>
                </div>
                <div className="sales-summary-card card-billable" onClick={() => setActiveTab("men")} style={{ background: '#e0f2fe', borderColor: '#bae6fd', cursor: 'pointer' }}>
                    <div className="sales-summary-icon" style={{ color: '#0369a1' }}>
                        <Icon icon="mdi:gender-male" width="26" />
                    </div>
                    <div className="sales-summary-info">
                        <h3>Men</h3>
                        <p className="sales-summary-value">{summary.men || 0}</p>
                    </div>
                </div>
                <div className="sales-summary-card card-nonbillable" onClick={() => setActiveTab("women")} style={{ background: '#fce7f3', borderColor: '#fbcfe8', cursor: 'pointer' }}>
                    <div className="sales-summary-icon" style={{ color: '#be185d' }}>
                        <Icon icon="mdi:gender-female" width="26" />
                    </div>
                    <div className="sales-summary-info">
                        <h3>Women</h3>
                        <p className="sales-summary-value">{summary.women || 0}</p>
                    </div>
                </div>
                <div className="sales-summary-card" onClick={() => setActiveTab("others")} style={{ background: '#fffbeb', borderColor: '#fde68a', cursor: 'pointer' }}>
                    <div className="sales-summary-icon" style={{ color: '#d97706' }}>
                        <Icon icon="mdi:gender-transgender" width="26" />
                    </div>
                    <div className="sales-summary-info">
                        <h3>Others</h3>
                        <p className="sales-summary-value">{summary.others || 0}</p>
                    </div>
                </div>
                <div className="sales-summary-card" onClick={() => setActiveTab("membership")} style={{ background: '#f5f3ff', borderColor: '#ddd6fe', cursor: 'pointer' }}>
                    <div className="sales-summary-icon" style={{ color: '#7c3aed' }}>
                        <Icon icon="mdi:card-membership" width="26" />
                    </div>
                    <div className="sales-summary-info">
                        <h3>Membership</h3>
                        <p className="sales-summary-value">{summary.membershipCustomers || 0}</p>
                    </div>
                </div>
                <div className="sales-summary-card" onClick={() => setActiveTab("non-membership")} style={{ background: '#f8fafc', borderColor: '#cbd5e1', cursor: 'pointer' }}>
                    <div className="sales-summary-icon" style={{ color: '#475569' }}>
                        <Icon icon="mdi:account-outline" width="26" />
                    </div>
                    <div className="sales-summary-info">
                        <h3>Non-Membership</h3>
                        <p className="sales-summary-value">{summary.nonMembershipCustomers || 0}</p>
                    </div>
                </div>
                <div className="sales-summary-card" onClick={() => setActiveTab("inactive")} style={{ background: '#fee2e2', borderColor: '#fecaca', cursor: 'pointer' }}>
                    <div className="sales-summary-icon" style={{ color: '#b91c1c' }}>
                        <Icon icon="mdi:account-off" width="26" />
                    </div>
                    <div className="sales-summary-info">
                        <h3>Inactive Customers</h3>
                        <p className="sales-summary-value">{summary.inactiveCustomers || 0}</p>
                    </div>
                </div>
            </div>

            {/* ── Tabs Section ── */}
            <div className="sales-report-tabs-wrapper" style={{ marginBottom: 20 }}>
                <div className="sales-report-tabs">
                    <button
                        className={`sales-report-tab ${activeTab === "all" ? "active" : ""}`}
                        onClick={() => setActiveTab("all")}
                    >
                        All Customers
                    </button>
                    <button
                        className={`sales-report-tab ${activeTab === "men" ? "active" : ""}`}
                        onClick={() => setActiveTab("men")}
                    >
                        Men
                    </button>
                    <button
                        className={`sales-report-tab ${activeTab === "women" ? "active" : ""}`}
                        onClick={() => setActiveTab("women")}
                    >
                        Women
                    </button>
                    <button
                        className={`sales-report-tab ${activeTab === "others" ? "active" : ""}`}
                        onClick={() => setActiveTab("others")}
                    >
                        Others
                    </button>
                    <button
                        className={`sales-report-tab ${activeTab === "membership" ? "active" : ""}`}
                        onClick={() => setActiveTab("membership")}
                    >
                        Membership
                    </button>
                    <button
                        className={`sales-report-tab ${activeTab === "non-membership" ? "active" : ""}`}
                        onClick={() => setActiveTab("non-membership")}
                    >
                        Non-Membership
                    </button>
                    <button
                        className={`sales-report-tab ${activeTab === "inactive" ? "active" : ""}`}
                        onClick={() => setActiveTab("inactive")}
                    >
                        Inactive Customers
                    </button>
                    <button
                        className={`sales-report-tab ${activeTab === "new" ? "active" : ""}`}
                        onClick={() => setActiveTab("new")}
                    >
                        New Customers
                    </button>
                    <button
                        className={`sales-report-tab ${activeTab === "old" ? "active" : ""}`}
                        onClick={() => setActiveTab("old")}
                    >
                        Old Customers
                    </button>
                    <button
                        className={`sales-report-tab ${activeTab === "frequent-services" ? "active" : ""}`}
                        onClick={() => setActiveTab("frequent-services")}
                    >
                        Frequent Services
                    </button>
                </div>
            </div>

            {/* ── Branch Filter ── */}
            {isSuperAdmin && (
                <div className="sales-filter-panel" style={{ marginBottom: 20 }}>
                    <div className="filter-grid" style={{ gridTemplateColumns: "1fr" }}>
                        <div className="filter-item" style={{ maxWidth: '300px' }}>
                            <label>Branch</label>
                            <select
                                className="filter-select"
                                value={filterStoreId}
                                onChange={e => setFilterStoreId(e.target.value)}
                            >
                                <option value="">All Branches</option>
                                {storeList.map(s => (
                                    <option key={s._id} value={s._id}>{s.branchName || s.storeName}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Table Card ── */}
            <div className="sales-table-card">
                <div className="table-header-row">
                    <h2 className="table-title">Customer Records</h2>
                    <div className="table-controls">
                        <div className="search-wrapper">
                            <Icon icon="mdi:magnify" className="search-icon-inside" />
                            <input
                                type="text"
                                className="table-search-input"
                                placeholder="Search customers..."
                                value={tableSearch}
                                onChange={e => setTableSearch(e.target.value)}
                            />
                        </div>
                        <div className="column-toggler-wrapper" ref={columnTogglerRef}>
                            <button
                                className="column-toggle-btn"
                                onClick={() => setShowColumnToggler(v => !v)}
                            >
                                <Icon icon="mdi:table-column" width="16" />
                                Columns
                                <Icon icon={showColumnToggler ? "mdi:chevron-up" : "mdi:chevron-down"} width="14" />
                            </button>
                            {showColumnToggler && (
                                <div className="column-toggler-dropdown">
                                    {tabColumns.filter(c => c !== "action").map(col => (
                                        <label key={col} className="column-toggle-item">
                                            <input
                                                type="checkbox"
                                                checked={visibleColumns[col]}
                                                onChange={() => setVisibleColumns(prev => ({ ...prev, [col]: !prev[col] }))}
                                            />
                                            {COLUMN_LABELS[col]}
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="sales-table-wrapper">
                    {loading ? (
                        <div className="sales-loading-state">
                            <div className="loading-spinner" />
                            <span>Loading customer report...</span>
                        </div>
                    ) : data.length === 0 ? (
                        <div className="sales-empty-state">
                            <div className="empty-icon">👥</div>
                            <p>No customer records found matching the selected filters.</p>
                        </div>
                    ) : (
                        <table className="sales-data-table">
                            <thead>
                                <tr>
                                    <th>S.No</th>
                                    {tabColumns.filter(c => visibleColumns[c] || c === "action").map(col => (
                                        <th key={col}>{COLUMN_LABELS[col]}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((row, idx) => {
                                    const currentColumns = tabColumns.filter(c => visibleColumns[c] || c === "action");
                                    return (
                                        <tr key={row._id || idx}>
                                            <td style={{ color: "#94a3b8", fontWeight: 600 }}>
                                                {(currentPage - 1) * rowsPerPage + idx + 1}
                                            </td>
                                            {currentColumns.includes("name") && (
                                                <td style={{ fontWeight: 600, color: "#334155" }}>
                                                    {row.name || "—"}
                                                </td>
                                            )}
                                            {currentColumns.includes("phoneNumber") && (
                                                <td>{row.phoneNumber || "—"}</td>
                                            )}
                                            {currentColumns.includes("gender") && (
                                                <td style={{ textTransform: "capitalize" }}>{row.gender || "—"}</td>
                                            )}
                                            {currentColumns.includes("membership") && (
                                                <td>
                                                    {row.hasActiveMembership ? (
                                                        <span className="status-badge" style={{ background: "rgba(16, 185, 129, 0.1)", color: "#10b981", border: "1px solid rgba(16, 185, 129, 0.2)" }}>
                                                            {row.activeMembershipName || "Member"}
                                                        </span>
                                                    ) : (
                                                        <span className="status-badge" style={{ background: "rgba(100, 116, 139, 0.1)", color: "#64748b", border: "1px solid rgba(100, 116, 139, 0.2)" }}>
                                                            Non-Member
                                                        </span>
                                                    )}
                                                </td>
                                            )}
                                            {currentColumns.includes("totalSpend") && (
                                                <td style={{ color: "#3c0008", fontWeight: 700 }}>
                                                    {(row.totalSpend)}
                                                </td>
                                            )}
                                            {currentColumns.includes("totalBookings") && (
                                                <td>
                                                    <span style={{ background: "#f1f5f9", padding: "4px 10px", borderRadius: "8px", fontWeight: 600 }}>
                                                        {row.totalBookings || 0}
                                                    </span>
                                                </td>
                                            )}
                                            {currentColumns.includes("lastActiveAt") && (
                                                <td>{formatDate(row.lastActiveAt)}</td>
                                            )}
                                            {isSuperAdmin && currentColumns.includes("branchName") && (
                                                <td>{row.branchName || "—"}</td>
                                            )}
                                            {currentColumns.includes("isActive") && (
                                                <td>
                                                    <span className={`status-badge ${row.isActive ? "badge-billable" : "badge-nonbillable"}`}>
                                                        {row.isActive ? "Active" : "Inactive"}
                                                    </span>
                                                </td>
                                            )}
                                            {currentColumns.includes("createdAt") && (
                                                <td>{formatDate(row.createdAt)}</td>
                                            )}
                                            {currentColumns.includes("serviceName") && (
                                                <td style={{ fontWeight: 600 }}>{row.serviceName || "—"}</td>
                                            )}
                                            {currentColumns.includes("usageCount") && (
                                                <td>
                                                    <span style={{ background: "#e0e7ff", color: "#3730a3", padding: "4px 10px", borderRadius: "8px", fontWeight: 600 }}>
                                                        {row.usageCount || 0} Times
                                                    </span>
                                                </td>
                                            )}
                                            {currentColumns.includes("totalSpent") && (
                                                <td style={{ color: "#065f46", fontWeight: 700 }}>
                                                    {row.totalSpent?.toFixed(2) || "0.00"}
                                                </td>
                                            )}
                                            {currentColumns.includes("firstServiceDate") && (
                                                <td>{formatDate(row.firstServiceDate)}</td>
                                            )}
                                            {currentColumns.includes("lastServiceDate") && (
                                                <td>{formatDate(row.lastServiceDate)}</td>
                                            )}
                                            {currentColumns.includes("action") && (
                                                <td>
                                                    <button
                                                        className="action-view-btn"
                                                        title="View Details"
                                                        onClick={() => setSelectedRecord(row)}
                                                    >
                                                        <Icon icon="mdi:eye-outline" width="16" />
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Pagination */}
                {!loading && totalItems > 0 && (
                    <div className="table-pagination-row">
                        <span className="pagination-info">
                            Showing {startItem}–{endItem} of {totalItems} entries
                        </span>
                        <div className="pagination-controls">
                            <select
                                className="page-size-select"
                                value={rowsPerPage}
                                onChange={e => {
                                    setRowsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                            >
                                {[10, 25, 50, 100].map(n => (
                                    <option key={n} value={n}>{n} / page</option>
                                ))}
                            </select>
                            <button
                                className="page-btn"
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                            >
                                <Icon icon="mdi:chevron-double-left" width="16" />
                            </button>
                            <button
                                className="page-btn"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                            >
                                <Icon icon="mdi:chevron-left" width="16" />
                            </button>
                            {renderPageNumbers()}
                            <button
                                className="page-btn"
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                            >
                                <Icon icon="mdi:chevron-right" width="16" />
                            </button>
                            <button
                                className="page-btn"
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={currentPage === totalPages}
                            >
                                <Icon icon="mdi:chevron-double-right" width="16" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ── View Modal ── */}
            {selectedRecord && (
                <div className="modal-overlay" onClick={() => setSelectedRecord(null)}>
                    <div className="expense-modal" onClick={e => e.stopPropagation()}>
                        <div className="expense-modal-header" style={{ background: 'linear-gradient(135deg, #3c0008 0%, #6b0010 100%)' }}>
                            <div>
                                <h2>Customer Details</h2>
                                <p>{selectedRecord.name || "Customer Record"}</p>
                            </div>
                            <button className="modal-close-btn" onClick={() => setSelectedRecord(null)}>
                                <Icon icon="mdi:close" width="20" />
                            </button>
                        </div>
                        <div className="expense-modal-body">
                            <div className={`expense-type-banner ${selectedRecord.isActive ? "billable" : "nonbillable"}`}>
                                <Icon icon={selectedRecord.isActive ? "mdi:account-check" : "mdi:account-off"} width="20" />
                                {selectedRecord.isActive ? "Active" : "Inactive"} Customer
                            </div>

                            <div>
                                <div className="modal-section-title">
                                    <Icon icon="mdi:information-outline" width="14" />
                                    Profile Information
                                </div>
                                <div className="modal-detail-list">
                                    <div className="modal-detail-row">
                                        <span className="detail-label">Name</span>
                                        <span className="detail-value">{selectedRecord.name || "—"}</span>
                                    </div>
                                    <div className="modal-detail-row">
                                        <span className="detail-label">Phone</span>
                                        <span className="detail-value">{selectedRecord.phoneNumber || "—"}</span>
                                    </div>
                                    <div className="modal-detail-row">
                                        <span className="detail-label">Gender</span>
                                        <span className="detail-value" style={{ textTransform: 'capitalize' }}>{selectedRecord.gender || "—"}</span>
                                    </div>
                                    <div className="modal-detail-row">
                                        <span className="detail-label">Membership</span>
                                        <span className="detail-value">
                                            {selectedRecord.hasActiveMembership ? (
                                                <span style={{ color: "#10b981", fontWeight: "bold" }}>
                                                    {selectedRecord.activeMembershipName || "Active Member"}
                                                </span>
                                            ) : "Non-Member"}
                                        </span>
                                    </div>
                                    <div className="modal-detail-row">
                                        <span className="detail-label">Branch</span>
                                        <span className="detail-value">{selectedRecord.branchName || "—"}</span>
                                    </div>
                                    <div className="modal-detail-row">
                                        <span className="detail-label">Total Spend</span>
                                        <span className="detail-value highlight">{(selectedRecord.totalSpend)}</span>
                                    </div>
                                    <div className="modal-detail-row">
                                        <span className="detail-label">Total Bookings</span>
                                        <span className="detail-value">{selectedRecord.totalBookings || 0} Bookings</span>
                                    </div>
                                    <div className="modal-detail-row">
                                        <span className="detail-label">Last Active</span>
                                        <span className="detail-value">{formatDate(selectedRecord.lastActiveAt)}</span>
                                    </div>
                                    <div className="modal-detail-row">
                                        <span className="detail-label">Since</span>
                                        <span className="detail-value">{formatDate(selectedRecord.createdAt)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
